// FILE LOCATION: src/App.js
// Main app component for browser-only DocWeb

import React, { useState, useEffect, useCallback } from "react";
import mammoth from "mammoth/mammoth.browser";
import "./App.css";
import DocWebLogo from "./components/DocWebLogo";
import { processDocument, createHTMLFile } from "./utils/documentProcessor";
import { loadZipPackage, updateManifestAndZip } from "./utils/zipProcessor";
import {
  saveFilesToFolder,
  saveImagesToFolder,
  getPermissionStatus,
  requestPermission,
  saveZipToFolder,
} from "./utils/fileSystemHelper";
import {
  extractQuestionBankToCSV,
  saveCsvToFolder,
} from "./utils/csvConverter";
import { fetchRegistry, parseAssetOverrides } from "./utils/assetRegistry";
import { Settings, BookOpen, X, FileText, Layout, Code, Layers, Video, Save, Palette, Archive } from "lucide-react";

function App() {
  // State management
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("No file selected");
  const [folderHandle, setFolderHandle] = useState(null);
  const [folderName, setFolderName] = useState("No folder selected");
  const [selectedZipFile, setSelectedZipFile] = useState(null);
  const [zipName, setZipName] = useState("No package selected");
  const [zipInstance, setZipInstance] = useState(null);
  const [manifestDoc, setManifestDoc] = useState(null);
  const [detectedManifestPages, setDetectedManifestPages] = useState([]);
  const [bannerType, setBannerType] = useState("none");
  const [footerType, setFooterType] = useState("None");
  const [prefixType, setPrefixType] = useState("None");
  const [customName, setCustomName] = useState("");

  // NEW STATES
  const [includeH1, setIncludeH1] = useState(false);
  const [usePlaceholders, setUsePlaceholders] = useState(true);
  const [keepStartHereName, setKeepStartHereName] = useState(true);

  const [visibility, setVisibility] = useState({
    all: false,
    dev: false,
    id: false,
    inst: false,
  });
  const [generateCsv, setGenerateCsv] = useState(false);
  const [stripRules, setStripRules] = useState([]);
  const [stripFileName, setStripFileName] = useState("No file selected");
  const [assetOverrides, setAssetOverrides] = useState({});

  const [useLegacyAssets, setUseLegacyAssets] = useState(true);
  const [assetRegistry, setAssetRegistry] = useState({});
  const [assetRegistryStatus, setAssetRegistryStatus] = useState('idle');
  const [assetRegistryError, setAssetRegistryError] = useState('');

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [configsOpen, setConfigsOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [showWiki, setShowWiki] = useState(false);
  const [wikiActiveTab, setWikiActiveTab] = useState("started");

  // Saved configurations
  const [savedConfigs, setSavedConfigs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('docweb_configs') || '{}'); }
    catch { return {}; }
  });
  const [configName, setConfigName] = useState('');
  const [selectedConfig, setSelectedConfig] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [customCssType, setCustomCssType] = useState("None");
  const [customCssName, setCustomCssName] = useState("");
  const [showCssWarning, setShowCssWarning] = useState(() => {
    return localStorage.getItem("docweb_show_css_warning") !== "false";
  });
  const [showCssWarningModal, setShowCssWarningModal] = useState(false);
  const [dontShowCssWarningCheckbox, setDontShowCssWarningCheckbox] = useState(false);

  useEffect(() => {
    localStorage.setItem('docweb_advanced_open', String(advancedOpen));
  }, [advancedOpen]);

  useEffect(() => {
    localStorage.setItem("docweb_show_css_warning", String(showCssWarning));
  }, [showCssWarning]);

  const gatherCurrentSettings = () => ({
    bannerType, footerType, prefixType, customName,
    includeH1, usePlaceholders, keepStartHereName,
    visibility, generateCsv, useLegacyAssets,
    customCssType, customCssName,
  });

  const applySettings = (settings) => {
    if (settings.bannerType !== undefined) setBannerType(settings.bannerType);
    if (settings.footerType !== undefined) setFooterType(settings.footerType);
    if (settings.prefixType !== undefined) setPrefixType(settings.prefixType);
    if (settings.customName !== undefined) setCustomName(settings.customName);
    if (settings.includeH1 !== undefined) setIncludeH1(settings.includeH1);
    if (settings.usePlaceholders !== undefined) setUsePlaceholders(settings.usePlaceholders);
    if (settings.keepStartHereName !== undefined) setKeepStartHereName(settings.keepStartHereName);
    if (settings.visibility !== undefined) setVisibility(settings.visibility);
    if (settings.generateCsv !== undefined) setGenerateCsv(settings.generateCsv);
    if (settings.useLegacyAssets !== undefined) setUseLegacyAssets(settings.useLegacyAssets);
    if (settings.customCssType !== undefined) setCustomCssType(settings.customCssType);
    if (settings.customCssName !== undefined) setCustomCssName(settings.customCssName);
  };

  const handleSaveConfig = () => {
    const name = configName.trim();
    if (!name) return;
    const updated = { ...savedConfigs, [name]: gatherCurrentSettings() };
    setSavedConfigs(updated);
    localStorage.setItem('docweb_configs', JSON.stringify(updated));
    setConfigName('');
    setSelectedConfig(name);
  };

  const handleSelectConfig = (name) => {
    setSelectedConfig(name);
    if (name && savedConfigs[name]) {
      applySettings(savedConfigs[name]);
    }
  };

  const handleDeleteConfig = () => {
    if (!selectedConfig || !savedConfigs[selectedConfig]) return;
    const updated = { ...savedConfigs };
    delete updated[selectedConfig];
    setSavedConfigs(updated);
    localStorage.setItem('docweb_configs', JSON.stringify(updated));
    setSelectedConfig('');
  };

  const handleClearLocalStorage = () => {
    Object.keys(localStorage)
      .filter(k => k.startsWith('docweb_'))
      .forEach(k => localStorage.removeItem(k));
    setSavedConfigs({});
    setSelectedConfig('');
    setShowClearConfirm(false);
    setShowPrefs(false);
  };

  const handleExportSettings = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(gatherCurrentSettings(), null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", "docweb-settings.json");
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  const handleImportSettings = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const settings = JSON.parse(e.target.result);
        applySettings(settings);
        const configNameFromImport = file.name.replace(/\.json$/i, "");
        setSavedConfigs((prev) => {
          const updated = { ...prev, [configNameFromImport]: settings };
          localStorage.setItem('docweb_configs', JSON.stringify(updated));
          return updated;
        });
        setSelectedConfig(configNameFromImport);
        setFeedbackMessage("Profile imported successfully");
      } catch (err) {
        setFeedbackMessage("Failed to parse settings JSON file");
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [progressStage, setProgressStage] = useState('');
  const [progressPct, setProgressPct]     = useState(0);
  const [progressDetail, setProgressDetail] = useState('');

  // Load asset registry from D2L
  const handleLoadAssets = useCallback(async () => {
    setAssetRegistryStatus('loading');
    try {
      const registry = await fetchRegistry(useLegacyAssets);
      setAssetRegistry(registry);
      setAssetRegistryStatus('loaded');
    } catch (err) {
      setAssetRegistry({});
      setAssetRegistryStatus('error');
      setAssetRegistryError(err.message);
      console.warn('Asset registry load failed:', err.message);
    }
  }, [useLegacyAssets]);

  // Auto-load on mount and whenever the legacy toggle changes
  useEffect(() => {
    handleLoadAssets();
  }, [handleLoadAssets]);

  // Handle folder selection using File System Access API
  const handleZipChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      setSelectedZipFile(file);
      setZipName(file.name);
      setFeedbackMessage("Loading Brightspace package...");
      const result = await loadZipPackage(file);
      setZipInstance(result.zipInstance);
      setManifestDoc(result.manifestDoc);
      setDetectedManifestPages(result.pages);
      setFeedbackMessage("Brightspace package loaded successfully");
    } catch (error) {
      setSelectedZipFile(null);
      setZipName("No package selected");
      setZipInstance(null);
      setManifestDoc(null);
      setDetectedManifestPages([]);
      setFeedbackMessage("Error loading Brightspace package: " + error.message);
    }
  };

  const handleClearZip = () => {
    setSelectedZipFile(null);
    setZipName("No package selected");
    setZipInstance(null);
    setManifestDoc(null);
    setDetectedManifestPages([]);
    setFeedbackMessage("");
  };

  const handleSelectFolder = async () => {
    if (!("showDirectoryPicker" in window)) {
      setFeedbackMessage(
        "❌ Your browser does not support the File System Access API. Please use Chrome 86+ or Edge 86+"
      );
      return;
    }

    try {
      const handle = await window.showDirectoryPicker();
      setFolderHandle(handle);
      setFolderName(handle.name);
      setFeedbackMessage(`Folder selected: ${handle.name}`);
    } catch (error) {
      if (error.name !== "AbortError") {
        setFeedbackMessage("Error selecting folder: " + error.message);
      }
    }
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
      setFeedbackMessage("");
    } else {
      setSelectedFile(null);
      setFileName("No file selected");
    }
  };

  // Handle strip rules TXT file
  const handleStripFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setStripRules([]);
      setStripFileName("No file selected");
      return;
    }
    setStripFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const allLines = e.target.result.split(/\r?\n/).map(l => l.trim()).filter(Boolean);

      // @lines → asset overrides  (e.g. "@accordion | theme-blue-600, icon-discInfo")
      setAssetOverrides(parseAssetOverrides(allLines));

      // remaining lines → strip / remap rules  (skip # comments and @lines)
      const rules = allLines
        .filter(line => !line.startsWith('@') && !line.startsWith('#'))
        .map((line) => {
          const pipeIdx = line.lastIndexOf("|");
          if (pipeIdx !== -1) {
            return {
              phrase: line.slice(0, pipeIdx).trim(),
              className: line.slice(pipeIdx + 1).trim(),
            };
          }
          return { phrase: line, className: "" };
        })
        .filter((rule) => rule.phrase.length > 0);
      setStripRules(rules);
    };
    reader.readAsText(file);
  };

  // Handle visibility checkbox changes
  const handleVisibilityChange = (type) => {
    setVisibility((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const getCustomCssPath = () => {
    if (customCssType === "None" || !customCssName.trim()) {
      return "";
    }
    let name = customCssName.trim();
    if (!name.endsWith(".css")) {
      name += ".css";
    }
    if (customCssType === "Local") {
      return `./ZZ_technical-files/css-add-on/${name}`;
    }
    if (customCssType === "Shared") {
      return `/shared/online/styles/standardized/add-ons/${name}`;
    }
    return "";
  };

  const handleConfirmConversionWithoutCss = async () => {
    setShowCssWarningModal(false);
    if (dontShowCssWarningCheckbox) {
      setShowCssWarning(false);
    }
    await performConversion();
  };

  const handleConvert = async () => {
    if (!selectedFile) {
      setFeedbackMessage("Please select a DOCX file first.");
      return;
    }
    if (!folderHandle) {
      setFeedbackMessage("Please select an output folder first.");
      return;
    }
    if (customCssType === "None" && showCssWarning) {
      setDontShowCssWarningCheckbox(false);
      setShowCssWarningModal(true);
    } else {
      await performConversion();
    }
  };

  const performConversion = async () => {
    if (!selectedFile) {
      setFeedbackMessage("Please select a DOCX file first.");
      return;
    }

    if (!folderHandle) {
      setFeedbackMessage("Please select an output folder first.");
      return;
    }

    setIsConverting(true);
    setFeedbackMessage('');
    setProgressStage('Starting…');
    setProgressDetail('');
    setProgressPct(0);

    // Yields to the renderer so React can paint the progress UI between steps
    const yieldUI = () => new Promise(r => setTimeout(r, 0));
    const updateProgress = async (pct, stage, detail = '') => {
      setProgressPct(pct);
      setProgressStage(stage);
      setProgressDetail(detail);
      await yieldUI();
    };

    try {
      await updateProgress(2, 'Checking folder permission…');

      // Ensure write permission is granted — prompts the user if needed
      const permStatus = await getPermissionStatus(folderHandle);
      if (permStatus !== "granted") {
        const requested = await requestPermission(folderHandle);
        if (requested !== "granted") {
          setFeedbackMessage(
            "❌ Write permission was denied for the selected folder. Please re-select the folder and allow access."
          );
          setIsConverting(false);
          return;
        }
      }

      await updateProgress(5, 'Reading DOCX file…', selectedFile.name);
      const arrayBuffer = await selectedFile.arrayBuffer();

      // We will capture extracted images here if placeholders are off
      const extractedImages = [];
      let imageCounter = 1;

      // Configure Mammoth to extract images cleanly
      const options = {
        convertImage: mammoth.images.imgElement(function (image) {
          return image.read("base64").then(function (imageBuffer) {
            const ext = image.contentType.split("/")[1] || "png";
            const filename = `image_${String(imageCounter).padStart(
              3,
              "0"
            )}.${ext}`;
            imageCounter++;

            // Save for the FileSystem API to dump later
            extractedImages.push({
              name: filename,
              data: imageBuffer,
              contentType: image.contentType,
            });

            return {
              src: `../images/${filename}`,
              alt: "Document image",
            };
          });
        }),
      };

      await updateProgress(15, 'Converting Word to HTML…', 'Running mammoth.js');
      const result = await mammoth.convertToHtml({ arrayBuffer }, options);
      const rawHtml = result.value;

      // Process the HTML document
      const parser = new DOMParser();
      const doc = parser.parseFromString(
        `<!DOCTYPE html><body>${rawHtml}</body>`,
        "text/html"
      );

      // processDocument now reports its own sub-stages between 30 % and 75 %
      const docStartPct = 30, docEndPct = 75;
      let docStepCount = 0;
      const expectedSteps = 9;
      const docProgressCb = async (label) => {
        docStepCount++;
        const pct = docStartPct + ((docEndPct - docStartPct) * docStepCount) / expectedSteps;
        await updateProgress(Math.min(pct, docEndPct), 'Processing document…', label);
      };

      const processedDoc = await processDocument(
        doc,
        visibility,
        usePlaceholders,
        stripRules,
        assetRegistry,
        assetOverrides,
        docProgressCb
      );

      await updateProgress(78, 'Splitting into sections…');
      const sections = splitByH1(processedDoc);

      if (sections.length === 0) {
        setFeedbackMessage(
          "No H1 headings found. Document must have H1 headings to split into sections."
        );
        setIsConverting(false);
        return;
      }

      await updateProgress(82, 'Building HTML files…', `${sections.length} page${sections.length !== 1 ? 's' : ''}`);
      let overrideIndex = 0;
      const resolvedCssPath = getCustomCssPath();
      const files = sections.map((section, index) => {
        const htmlContent = createHTMLFile(
          section.title,
          section.content,
          bannerType,
          footerType,
          prefixType,
          useLegacyAssets,
          resolvedCssPath
        );

        const normalizedTitle = section.title
          .trim()
          .toLowerCase()
          .replace(/[^\w\s-]/g, "")
          .replace(/\s+/g, " ");
        const isStartHere = keepStartHereName && (normalizedTitle === "start here" || normalizedTitle === "start-here");

        const fileName = isStartHere
          ? "Start Here.html"
          : formatFileName(section.title, prefixType, customName, overrideIndex++);

        return {
          name: fileName,
          content: htmlContent,
        };
      });

      if (zipInstance) {
        await updateProgress(90, 'Updating Brightspace package…');
        const updatedBlob = await updateManifestAndZip(zipInstance, manifestDoc, files, usePlaceholders ? [] : extractedImages, resolvedCssPath);
        
        await updateProgress(95, 'Saving package ZIP…');
        const newZipName = selectedZipFile.name.replace(/\.zip$/i, "_updated.zip");
        await saveZipToFolder(folderHandle, updatedBlob, newZipName);

        await updateProgress(100, 'Done!', '');
        setFeedbackMessage(`✅ Successfully updated Brightspace package and saved ${newZipName} to ${folderName}!`);
      } else {
        let csvCount = 0;
        if (generateCsv) {
          await updateProgress(86, 'Generating CSV question banks…');
          for (const section of sections) {
            try {
              const csvContent = extractQuestionBankToCSV(section.content);
              if (csvContent && csvContent.trim()) {
                const result = await saveCsvToFolder(
                  folderHandle,
                  csvContent,
                  section.title
                );
                if (result.success) csvCount++;
              }
            } catch (csvError) {
              console.error("CSV generation error:", csvError);
            }
          }
        }

        await updateProgress(92, 'Saving HTML files to folder…', folderName);
        await saveFilesToFolder(folderHandle, files);

        if (customCssType === "Local" && customCssName.trim()) {
          let name = customCssName.trim();
          if (!name.endsWith(".css")) {
            name += ".css";
          }
          try {
            const zzHandle = await folderHandle.getDirectoryHandle('ZZ_technical-files', { create: true });
            const cssAddonHandle = await zzHandle.getDirectoryHandle('css-add-on', { create: true });
            try {
              await cssAddonHandle.getFileHandle(name);
            } catch (e) {
              const fileHandle = await cssAddonHandle.getFileHandle(name, { create: true });
              const writable = await fileHandle.createWritable();
              await writable.write("");
              await writable.close();
            }
          } catch (err) {
            console.error(err);
          }
        }

        let imgCount = 0;
        if (!usePlaceholders && extractedImages.length > 0) {
          await updateProgress(97, 'Saving extracted images…', `${extractedImages.length} image${extractedImages.length !== 1 ? 's' : ''}`);
          await saveImagesToFolder(folderHandle, extractedImages);
          imgCount = extractedImages.length;
        }

        await updateProgress(100, 'Done!', '');

        let message = `✅ Successfully saved ${files.length} HTML file(s)`;
        if (generateCsv && csvCount > 0) {
          message += `, ${csvCount} CSV file(s)`;
        }
        if (imgCount > 0) {
          message += `, and ${imgCount} extracted image(s)`;
        }
        message += ` to ${folderName}!`;

        setFeedbackMessage(message);
      }
    } catch (error) {
      console.error("Conversion error:", error);
      setFeedbackMessage(`❌ Error: ${error.message}`);
    } finally {
      setIsConverting(false);
      setProgressStage('');
      setProgressDetail('');
      setProgressPct(0);
    }
  };

  // Helper function to split document by H1 tags
  const splitByH1 = (document) => {
    const sections = [];
    const h1Elements = document.querySelectorAll("h1");

    h1Elements.forEach((h1, index) => {
      // Strip soft hyphens and other invisible Word artifacts from the title
      const title = h1.textContent.replace(/­/g, "").trim();
      const contentNodes = [];

      // Inject the H1 tag into the webpage content if selected
      if (includeH1) {
        contentNodes.push(h1.cloneNode(true));
      }

      let currentNode = h1.nextSibling;
      const nextH1 = h1Elements[index + 1];

      while (currentNode && currentNode !== nextH1) {
        if (currentNode.nodeType === 1) {
          // Element node
          contentNodes.push(currentNode.cloneNode(true));
        }
        currentNode = currentNode.nextSibling;
      }

      const content = contentNodes.map((node) => node.outerHTML).join("");

      sections.push({ title, content });
    });

    return sections;
  };

  const toTitleCase = (str) => {
    return str.replace(
      /\w\S*/g,
      (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
    );
  };

  const formatFileName = (title, prefix, customName, index) => {
    const cleanCustom = customName
      .replace(/[^\w\s]/gi, "")
      .replace(/\s+/g, " ")
      .trim();
    const cleanTitle = title
      .replace(/[^\w\s]/gi, "")
      .replace(/\s+/g, " ")
      .trim();
    const finalCustom = cleanCustom ? toTitleCase(cleanCustom) : "";
    const finalTitle = cleanTitle ? toTitleCase(cleanTitle) : "";
    const baseName = finalCustom || finalTitle;
    const sectionNumber = String(index + 1).padStart(2, "0");

    if (prefix !== "None") {
      const finalPrefix = toTitleCase(prefix);
      return `${finalPrefix} ${sectionNumber}${
        baseName ? " " + baseName : ""
      }.html`;
    }

    if (finalCustom) {
      return `${finalCustom} ${sectionNumber}.html`;
    }

    return `${finalTitle}.html`;
  };

  return (
    <div className="App">
      <div className="container">
        <div className="card-stack">
        <div className="card main-card">
          {/* Header controls left: configs toggle */}
          <div className="header-controls-left">
            <button
              className={`advanced-toggle ${configsOpen ? 'active' : ''}`}
              onClick={() => setConfigsOpen(o => !o)}
              disabled={isConverting}
              title="Saved configurations"
            >
              {configsOpen ? '▶ Hide' : '◀ Configs'}
            </button>
          </div>

          {/* Header controls right: gear + Advanced toggle */}
          <div className="header-controls">
            <button
              className="icon-button"
              onClick={() => setShowWiki(true)}
              disabled={isConverting}
              title="Wiki Manual"
              aria-label="Wiki Manual"
            >
              <BookOpen size={18} />
            </button>
            <button
              className="icon-button"
              onClick={() => setShowPrefs(true)}
              disabled={isConverting}
              title="Preferences"
              aria-label="Preferences"
            >
              <Settings size={18} />
            </button>
            <button
              className={`advanced-toggle ${advancedOpen ? 'active' : ''}`}
              onClick={() => setAdvancedOpen(o => !o)}
              disabled={isConverting}
              title="Advanced options"
            >
              {advancedOpen ? '◀ Hide' : 'Advanced ▶'}
            </button>
          </div>

          {/* Header */}
          <div className="header">
            <div className="logo-container">
              <DocWebLogo />
            </div>
            <div className="header-content">
              <p>Convert your documents to web-friendly formats</p>
            </div>
          </div>

          <div className="divider"></div>

          {/* File Upload Section */}
          <details open className="welcome-box">
            <summary className="welcome-summary">Welcome & Instructions</summary>
            <div className="welcome-content">
              <p>Welcome! DocWeb will allow you to convert your Word document into a series of D2L-friendly webpages.</p>
              <p>Upload a document using the button below to begin.</p>
              <p><strong>Note:</strong> DocWeb looks for Word headings in your document. If you want the document to be split into multiple webpages, use H1s within the Word document wherever a new page begins. Otherwise, DocWeb will make your document into a single webpage.</p>
            </div>
          </details>

          <h2 className="section-title">Document Selection</h2>
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <div className="file-input">
                <label htmlFor="docxFile" className="file-button">
                  Choose DOCX File
                </label>
                <input
                  type="file"
                  id="docxFile"
                  accept=".docx"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  disabled={isConverting}
                />
                <div className="file-name">{fileName}</div>
              </div>
            </div>
          </div>

          <div className="divider"></div>

          <h2 className="section-title">Brightspace ZIP Package (Optional)</h2>
          <div className="form-row">
            <div className="form-group zip-form-group">
              <div className="file-input">
                <label htmlFor="zipFile" className="file-button">
                  Choose Brightspace ZIP
                </label>
                <input
                  type="file"
                  id="zipFile"
                  accept=".zip"
                  onChange={handleZipChange}
                  className="hidden-file-input"
                  disabled={isConverting}
                />
                <div className="file-name">{zipName}</div>
              </div>
              {zipInstance && (
                <div className="package-info-card">
                  <h3 className="package-info-title">Package Loaded</h3>
                  <p className="package-info-details">
                    Pages detected: {detectedManifestPages.length}
                  </p>
                  <button
                    type="button"
                    className="action-button-alt danger clear-package-btn"
                    onClick={handleClearZip}
                    disabled={isConverting}
                  >
                    Clear Package
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="divider"></div>

          {/* Display Options Section */}
          <h2 className="section-title">Display Options</h2>

          {/* NEW: Display & Structural Options */}
          <div className="form-row" style={{ marginBottom: "1rem" }}>
            <div className="form-group" style={{ flex: "1 1 100%" }}>
              <div
                className="checkbox-item"
                style={{ marginBottom: "0.75rem" }}
              >
                <input
                  type="checkbox"
                  id="includeH1"
                  checked={includeH1}
                  onChange={(e) => setIncludeH1(e.target.checked)}
                  disabled={isConverting}
                />
                <label htmlFor="includeH1">
                  Include H1 title in the webpage body content
                </label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="usePlaceholders"
                  checked={usePlaceholders}
                  onChange={(e) => setUsePlaceholders(e.target.checked)}
                  disabled={isConverting}
                />
                <label htmlFor="usePlaceholders">
                  Use image placeholders instead of extracting actual images
                </label>
              </div>
              <div
                className="checkbox-item"
                style={{ marginTop: "0.75rem" }}
              >
                <input
                  type="checkbox"
                  id="keepStartHereName"
                  checked={keepStartHereName}
                  onChange={(e) => setKeepStartHereName(e.target.checked)}
                  disabled={isConverting}
                />
                <label htmlFor="keepStartHereName">
                  Exclude 'Start Here' page from custom prefixes (keep 'Start Here.html')
                </label>
              </div>
            </div>
          </div>

          <div className="form-row" style={{ alignItems: 'flex-start' }}>
            <div className="form-group">
              <label htmlFor="bannerSelect">Banner Type</label>
              <select
                id="bannerSelect"
                value={bannerType}
                onChange={(e) => setBannerType(e.target.value)}
                disabled={isConverting}
              >
                <option value="none">None</option>
                <option value="image">Image</option>
                <option value="iframe">Image Iframe</option>
                <option value="bannerTimeline">Banner Timeline</option>
                <option value="bannerPagination">Banner Pagination</option>
                <option value="progressive">Progressive Banner</option>
                <option value="timeline">Timeline</option>
                <option value="pagination">Pagination</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="footerSelect">Footer Type</label>
              <select
                id="footerSelect"
                value={footerType}
                onChange={(e) => setFooterType(e.target.value)}
                disabled={isConverting}
              >
                <option value="None">None</option>
                <option value="ClassicDiamond">Classic Diamond</option>
                <option value="Image">Image</option>
                <option value="Iframe">Image Iframe</option>
              </select>
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="prefixSelect">File Prefix</label>
              <select
                id="prefixSelect"
                value={prefixType}
                onChange={(e) => setPrefixType(e.target.value)}
                disabled={isConverting}
              >
                <option value="None">None</option>
                <option value="Module">Module</option>
                <option value="Week">Week</option>
                <option value="Session">Session</option>
                <option value="Unit">Unit</option>
                <option value="Lesson">Lesson</option>
              </select>
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="customName">Custom HTML File Name</label>
              <input
                type="text"
                id="customName"
                className="text-input"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Leave blank for H1 title"
                disabled={isConverting}
              />
              <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '0.35rem' }}>
                (i.e., "Module" = Module 01.html, etc.)
              </p>
            </div>
          </div>

          <div className="divider"></div>

          <h2 className="section-title">Add custom CSS to course</h2>
          <p className="css-description">
            If you would like to modify some aspects of the style of your course (optional), this could be done by adding an extra CSS file to your webpages. If you would like to do that, you would specify the name of the file below, and choose between a Local file or a Shared file.
          </p>
          <div className="css-info-box">
            <p>
              <strong>Shared:</strong> If your style customization will be needed in multiple courses, the CSS add-on file should be stored outside the course, in the Public Files folder. Send a ticket to onlinedevelopment@unb.ca, and a team member will add it there for you.
            </p>
            <p>
              <strong>Local (in course):</strong> If your style customization is only needed in one course, store it in the Manage Files area of your D2L course, in this folder: <code>/ZZ_technical-files/css-add-on/</code>
            </p>
          </div>
          <div className="radio-group">
            <div className="radio-item">
              <input
                type="radio"
                id="cssNone"
                name="customCssType"
                value="None"
                checked={customCssType === "None"}
                onChange={(e) => setCustomCssType(e.target.value)}
                disabled={isConverting}
              />
              <label htmlFor="cssNone">None</label>
            </div>
            <div className="radio-item">
              <input
                type="radio"
                id="cssLocal"
                name="customCssType"
                value="Local"
                checked={customCssType === "Local"}
                onChange={(e) => setCustomCssType(e.target.value)}
                disabled={isConverting}
              />
              <label htmlFor="cssLocal">Local Files</label>
            </div>
            <div className="radio-item">
              <input
                type="radio"
                id="cssShared"
                name="customCssType"
                value="Shared"
                checked={customCssType === "Shared"}
                onChange={(e) => setCustomCssType(e.target.value)}
                disabled={isConverting}
              />
              <label htmlFor="cssShared">Shared Files</label>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <input
                type="text"
                id="customCssName"
                className="text-input"
                value={customCssName}
                onChange={(e) => setCustomCssName(e.target.value)}
                placeholder="Custom CSS file name…"
                disabled={isConverting || customCssType === "None"}
              />
            </div>
          </div>

          <div className="divider"></div>

          {/* Asset Registry — legacy toggle only (details in Advanced) */}
          <h2 className="section-title">Asset Registry</h2>
          <div className="form-row">
            <div className="form-group">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="useLegacyAssets"
                  checked={useLegacyAssets}
                  onChange={(e) => setUseLegacyAssets(e.target.checked)}
                  disabled={isConverting}
                />
                <label htmlFor="useLegacyAssets">Use legacy asset library</label>
              </div>
            </div>
          </div>

          <div className="divider"></div>

          <h2 className="section-title">Output Folder</h2>
          <p className="output-folder-note">
            DocWeb will ask you to let it save the converted files (webpages) to your computer. Click the button below to choose a folder on your computer, where the webpages (HTML files) will be saved.
          </p>
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <button
                onClick={handleSelectFolder}
                className="folder-button"
                disabled={isConverting}
              >
                Select Output Folder
              </button>
              <div className="folder-name">{folderName}</div>
            </div>
          </div>
          <div className="divider"></div>

          {/* Progress UI — only shown during conversion */}
          {isConverting && (
            <div className="conversion-progress">
              <div className="progress-spinner"></div>
              <div className="progress-info">
                <div className="progress-stage">{progressStage}</div>
                {progressDetail && (
                  <div className="progress-detail">{progressDetail}</div>
                )}
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${progressPct}%` }}
                  ></div>
                </div>
                <div className="progress-pct">{Math.round(progressPct)}%</div>
              </div>
            </div>
          )}

          <div className="convert-info">
            <h3 className="convert-info-title">Ready to convert?</h3>
            <p className="convert-info-text">
              Once you've selected your preferred settings from the options above, click the button below to convert your Word doc to webpages!
            </p>
          </div>

          <div className="convert-container">
            <button
              className="convert-button"
              onClick={handleConvert}
              disabled={isConverting || !selectedFile || !folderHandle}
            >
              {isConverting 
                ? "Converting…" 
                : zipInstance 
                  ? "Convert and Save ZIP" 
                  : "Convert to HTML"}
            </button>
          </div>

          <div className="divider"></div>

          {/* Feedback Message */}
          {feedbackMessage && !isConverting && (
            <div className="feedback-message">{feedbackMessage}</div>
          )}
        </div>

        {/* ─── Configs Drawer (left) ───────────────────────────────────── */}
        <div className={`configs-drawer ${configsOpen ? 'open' : ''}`}>
          <button
            className="drawer-close-btn"
            onClick={() => setConfigsOpen(false)}
            title="Close configurations"
            aria-label="Close"
          >
            ✕
          </button>
          <h2 className="drawer-title">Saved Configurations</h2>

          <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '1rem' }}>
            Save your current settings under a name, then reload them any time from the dropdown.
          </p>

          <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.4rem', display: 'block' }}>
            Load a configuration
          </label>
          <select
            value={selectedConfig}
            onChange={e => handleSelectConfig(e.target.value)}
            className="config-select"
            disabled={isConverting}
          >
            <option value="">— Select to load —</option>
            {Object.keys(savedConfigs).map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          {selectedConfig && (
            <button
              className="config-btn config-btn-danger"
              onClick={handleDeleteConfig}
              disabled={isConverting}
              style={{ marginTop: '0.5rem', width: '100%' }}
            >
              Delete "{selectedConfig}"
            </button>
          )}

          <div className="divider" style={{ margin: '1.25rem 0' }}></div>

          <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.4rem', display: 'block' }}>
            Save current settings
          </label>
          <input
            type="text"
            className="text-input config-name-input"
            value={configName}
            onChange={e => setConfigName(e.target.value)}
            placeholder="Configuration name…"
            disabled={isConverting}
            onKeyDown={e => e.key === 'Enter' && handleSaveConfig()}
            style={{ marginBottom: '0.5rem' }}
          />
          <button
            className="config-btn config-btn-save"
            onClick={handleSaveConfig}
            disabled={isConverting || !configName.trim()}
            style={{ width: '100%' }}
          >
            Save Current Settings
          </button>

          <div className="divider"></div>

          <label className="config-label">
            Export / Import Settings
          </label>
          <div className="button-group">
            <button
              className="action-button-alt"
              onClick={handleExportSettings}
              disabled={isConverting}
            >
              Export Active Profile
            </button>
            <label htmlFor="importProfileInput" className="file-button-alt">
              Import Profile
            </label>
            <input
              type="file"
              id="importProfileInput"
              accept=".json"
              onChange={handleImportSettings}
              className="hidden-file-input"
              disabled={isConverting}
            />
          </div>
        </div>

        {/* ─── Advanced Drawer ─────────────────────────────────────────── */}
        <div className={`advanced-drawer ${advancedOpen ? 'open' : ''}`}>
          <button
            className="drawer-close-btn"
            onClick={() => setAdvancedOpen(false)}
            title="Close advanced options"
            aria-label="Close"
          >
            ✕
          </button>
          <h2 className="drawer-title">Advanced Options</h2>

          {/* Content Visibility */}
          <h2 className="section-title">Content Visibility</h2>
          <div className="form-row">
            <div className="form-group">
              <div className="visibility-options">
                <div className="checkbox-item">
                  <input type="checkbox" id="toggleAll" checked={visibility.all} onChange={() => handleVisibilityChange("all")} disabled={isConverting} />
                  <label htmlFor="toggleAll">All</label>
                </div>
                <div className="checkbox-item">
                  <input type="checkbox" id="toggleDev" checked={visibility.dev} onChange={() => handleVisibilityChange("dev")} disabled={isConverting} />
                  <label htmlFor="toggleDev">Developer</label>
                </div>
                <div className="checkbox-item">
                  <input type="checkbox" id="toggleID" checked={visibility.id} onChange={() => handleVisibilityChange("id")} disabled={isConverting} />
                  <label htmlFor="toggleID">Instructional Designer</label>
                </div>
                <div className="checkbox-item">
                  <input type="checkbox" id="toggleInst" checked={visibility.inst} onChange={() => handleVisibilityChange("inst")} disabled={isConverting} />
                  <label htmlFor="toggleInst">Instructor</label>
                </div>
              </div>
            </div>
          </div>

          <div className="divider"></div>

          {/* Question Bank Export */}
          <h2 className="section-title">Question Bank Export</h2>
          <div className="form-row">
            <div className="form-group">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="toggleCsv"
                  checked={generateCsv}
                  onChange={(e) => setGenerateCsv(e.target.checked)}
                  disabled={isConverting}
                />
                <label htmlFor="toggleCsv">Generate CSV files for question banks</label>
              </div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginTop: '0.5rem' }}>
                Creates D2L-compatible CSV files for any question banks found in the document.
              </p>
            </div>
          </div>

          <div className="divider"></div>

          {/* Strip / Remap Rules */}
          <h2 className="section-title">Content Strip / Remap Rules</h2>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-light)', marginBottom: '0.75rem' }}>
            Load a TXT file to remove or reclassify content. One rule per line:
          </p>
          <ul style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '1rem', paddingLeft: '1.25rem' }}>
            <li><code>This is an iframe.</code> — removes any element containing the text.</li>
            <li><code>Assignment document LINK(S) HERE | id</code> — applies <code>class="id"</code>.</li>
            <li><code>This text would be highlighted | mark</code> — wraps the text in <code>&lt;mark&gt;</code>.</li>
            <li><code>Some text | ALERT BOX theme-red-500</code> — assembles an asset (uppercase = asset name).</li>
          </ul>
          <div className="form-row">
            <div className="form-group">
              <div className="file-input">
                <label htmlFor="stripFile" className="file-button">Choose Strip Rules TXT</label>
                <input
                  type="file"
                  id="stripFile"
                  accept=".txt"
                  onChange={handleStripFileChange}
                  style={{ display: 'none' }}
                  disabled={isConverting}
                />
                <div className="file-name">{stripFileName}</div>
              </div>
              {stripRules.length > 0 && (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginTop: '0.5rem' }}>
                  {stripRules.length} rule{stripRules.length !== 1 ? 's' : ''} loaded
                  ({stripRules.filter(r => !r.className).length} strip,{' '}
                  {stripRules.filter(r => r.className).length} remap)
                </p>
              )}
            </div>
          </div>

          <div className="divider"></div>

          {/* Asset Registry — reload & count */}
          <h2 className="section-title">Asset Library</h2>
          <div className="form-row">
            <div className="form-group">
              <button
                onClick={handleLoadAssets}
                className="folder-button"
                disabled={isConverting || assetRegistryStatus === 'loading'}
              >
                {assetRegistryStatus === 'loading' ? 'Loading Assets…' : 'Reload Assets'}
              </button>
              {assetRegistryStatus === 'loaded' && (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginTop: '0.5rem' }}>
                  ✅ {Object.keys(assetRegistry).length} component{Object.keys(assetRegistry).length !== 1 ? 's' : ''} loaded
                  {useLegacyAssets ? ' (legacy)' : ' (current)'}
                </p>
              )}
              {assetRegistryStatus === 'error' && (
                <p style={{ fontSize: '0.85rem', color: '#c0392b', marginTop: '0.5rem' }}>
                  ⚠️ Could not load asset registry — {assetRegistryError}
                </p>
              )}
            </div>
          </div>
        </div>
        {/* ─── End drawer ──────────────────────────────────────────────── */}
        </div>
      </div>

      {/* Preferences modal */}
      {showPrefs && (
        <div className="prefs-backdrop" onClick={() => { setShowPrefs(false); setShowClearConfirm(false); }}>
          <div className="prefs-modal" onClick={e => e.stopPropagation()}>
            <h2>Preferences</h2>
            <div className="prefs-row">
              <span>Clear all saved configurations and settings</span>
              {!showClearConfirm ? (
                <button className="prefs-action-btn prefs-action-btn-danger" onClick={() => setShowClearConfirm(true)}>
                  Clear All
                </button>
              ) : (
                <div className="prefs-confirm-inline">
                  <span className="prefs-confirm-label">Are you sure? This will remove all saved settings.</span>
                  <button className="prefs-action-btn prefs-action-btn-danger" onClick={handleClearLocalStorage}>
                    Yes, Delete All
                  </button>
                  <button className="prefs-action-btn" onClick={() => setShowClearConfirm(false)}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
            <div className="prefs-row">
              <label htmlFor="cssWarningCheckbox">Show warning when converting with no custom CSS</label>
              <input
                type="checkbox"
                id="cssWarningCheckbox"
                checked={showCssWarning}
                onChange={(e) => setShowCssWarning(e.target.checked)}
              />
            </div>
            <button className="prefs-modal-close" onClick={() => { setShowPrefs(false); setShowClearConfirm(false); }}>
              Close
            </button>
          </div>
        </div>
      )}

      {showWiki && (
        <div className="wiki-backdrop" onClick={() => setShowWiki(false)}>
          <div className="wiki-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wiki-header">
              <h2>
                <BookOpen size={20} />
                DocWeb Manual & Wiki
              </h2>
              <button
                className="icon-button"
                onClick={() => setShowWiki(false)}
                title="Close manual"
              >
                <X size={18} />
              </button>
            </div>
            <div className="wiki-layout">
              <div className="wiki-sidebar">
                <button
                  className={`wiki-tab-btn ${wikiActiveTab === "started" ? "active" : ""}`}
                  onClick={() => setWikiActiveTab("started")}
                >
                  <FileText size={16} />
                  Getting Started
                </button>
                <button
                  className={`wiki-tab-btn ${wikiActiveTab === "splitting" ? "active" : ""}`}
                  onClick={() => setWikiActiveTab("splitting")}
                >
                  <Layout size={16} />
                  Page Splitting
                </button>
                <button
                  className={`wiki-tab-btn ${wikiActiveTab === "visibility" ? "active" : ""}`}
                  onClick={() => setWikiActiveTab("visibility")}
                >
                  <Layers size={16} />
                  Visibility Tags
                </button>
                <button
                  className={`wiki-tab-btn ${wikiActiveTab === "shortcodes" ? "active" : ""}`}
                  onClick={() => setWikiActiveTab("shortcodes")}
                >
                  <Code size={16} />
                  Shortcodes
                </button>
                <button
                  className={`wiki-tab-btn ${wikiActiveTab === "html-embeds" ? "active" : ""}`}
                  onClick={() => setWikiActiveTab("html-embeds")}
                >
                  <Video size={16} />
                  HTML & Embeds
                </button>
                <button
                  className={`wiki-tab-btn ${wikiActiveTab === "custom-css" ? "active" : ""}`}
                  onClick={() => setWikiActiveTab("custom-css")}
                >
                  <Palette size={16} />
                  Custom CSS
                </button>
                <button
                  className={`wiki-tab-btn ${wikiActiveTab === "brightspace" ? "active" : ""}`}
                  onClick={() => setWikiActiveTab("brightspace")}
                >
                  <Archive size={16} />
                  Brightspace Packages
                </button>
                <button
                  className={`wiki-tab-btn ${wikiActiveTab === "saved-configs" ? "active" : ""}`}
                  onClick={() => setWikiActiveTab("saved-configs")}
                >
                  <Save size={16} />
                  Saved Configurations
                </button>
              </div>
              <div className="wiki-content">
                {wikiActiveTab === "started" && (
                  <div className="wiki-section">
                    <h3>Getting Started</h3>
                    <p>
                      DocWeb is an instructional design tool that converts formatted Word documents (.docx) into web-friendly, responsive HTML pages. The tool automatically handles file splitting, layout creation, video embeds, and styles.
                    </p>
                    <p>
                      To convert a document, select a .docx file and specify an output folder. Adjust the display, banner, and prefix settings as needed, then click "Convert" to process and save the pages.
                    </p>
                    <div className="wiki-alert-info">
                      <p>
                        Always save your work directly to a clean output folder. Standard web assets (CSS files, logo images) will be automatically generated and linked.
                      </p>
                    </div>
                  </div>
                )}
                {wikiActiveTab === "splitting" && (
                  <div className="wiki-section">
                    <h3>Page Splitting</h3>
                    <p>
                      The app splits your Word document into individual HTML pages based on H1 headings. Every time the parser detects an H1 heading, it begins a new page.
                    </p>
                    <p>
                      You can customize the exported filenames using the file prefix dropdown or custom prefix text input. If you enable the "Exclude 'Start Here' page" option (enabled by default), a page titled "Start Here" will export as <code>Start Here.html</code> and the numbering sequence will start on the following page.
                    </p>
                  </div>
                )}
                {wikiActiveTab === "visibility" && (
                  <div className="wiki-section">
                    <h3>Visibility Tags</h3>
                    <p>
                      You can restrict the visibility of elements or paragraphs to specific target audiences by wrapping them in double, triple, or quadruple square brackets:
                    </p>
                    <ul>
                      <li><code>[[Developer Content]]</code> — Visible only to developers. Wraps the element in a "dev" class.</li>
                      <li><code>[[[Instructional Designer Content]]]</code> — Visible only to IDs. Wraps the element in an "id" class.</li>
                      <li><code>[[[[Instructor Content]]]]</code> — Visible only to instructors. Wraps the element in an "inst" class.</li>
                    </ul>
                    <p>
                      Use the "Content Visibility" toggles in the Advanced Options menu to show or hide these sections in the browser interface.
                    </p>
                  </div>
                )}
                {wikiActiveTab === "shortcodes" && (
                  <div className="wiki-section">
                    <h3>Shortcodes</h3>
                    <p>
                      Use shortcode blocks to create multi-column grids or interactive accordion containers directly from Word:
                    </p>
                    <pre>
                      <code>
                        [accordion-wrapper theme="accordion-blue"]{"\n"}
                        [accordion-title]Section Title[/accordion-title]{"\n"}
                        Accordion content goes here.{"\n"}
                        [/accordion-wrapper]
                      </code>
                    </pre>
                    <p>
                      To split content into two equal columns:
                    </p>
                    <pre>
                      <code>
                        [row-start]{"\n"}
                        Left column content.{"\n"}
                        [col-split]{"\n"}
                        Right column content.{"\n"}
                        [row-end]
                      </code>
                    </pre>
                  </div>
                )}
                {wikiActiveTab === "html-embeds" && (
                  <div className="wiki-section">
                    <h3>HTML & Embeds</h3>
                    <p>
                      If you write or paste HTML code directly into your Word document, DocWeb will automatically detect the tags and render them as live HTML rather than displaying them as plain text.
                    </p>
                    <p>
                      This is useful for embedding custom media players, scripts, or inline layouts. Commonly supported tags include:
                    </p>
                    <ul>
                      <li><code>&lt;iframe&gt;</code> — For video links (YouTube, Vimeo) or interactive slides.</li>
                      <li><code>&lt;div&gt;</code>, <code>&lt;span&gt;</code>, <code>&lt;p&gt;</code> — For custom styling blocks.</li>
                      <li><code>&lt;strong&gt;</code>, <code>&lt;em&gt;</code> — For typography formatting.</li>
                    </ul>
                    <div className="wiki-alert-warning">
                      <p>
                        Ensure your HTML markup is well-formed with closing tags. Single comparisons like x &lt; y are ignored and will not be parsed as HTML.
                      </p>
                    </div>
                  </div>
                )}
                {wikiActiveTab === "custom-css" && (
                  <div className="wiki-section">
                    <h3>Custom CSS</h3>
                    <p>
                      DocWeb supports styling customizations by automatically linking a custom CSS stylesheet to all generated webpage files.
                    </p>
                    <p>
                      In the CSS configuration area, you can choose between two main modes:
                    </p>
                    <ul>
                      <li><strong>Local File (in course):</strong> Used for customizations specific to a single course. Save your CSS file in the Manage Files directory of the D2L course under <code>/ZZ_technical-files/css-add-on/</code>.</li>
                      <li><strong>Shared File (across courses):</strong> Used for style settings shared across multiple courses. Store the stylesheet in the D2L Public Files folder. Request setup via a ticket to online development.</li>
                    </ul>
                    <div className="wiki-alert-info">
                      <p>
                        Specify only the stylesheet filename (without the path) in the input field. DocWeb will build the correct internal path links automatically in the output HTML files.
                      </p>
                    </div>
                  </div>
                )}
                {wikiActiveTab === "brightspace" && (
                  <div className="wiki-section">
                    <h3>Brightspace Packages</h3>
                    <p>
                      DocWeb allows importing and updating D2L Brightspace course packages (.zip) directly. This keeps the package structure and manifest intact while updating page contents.
                    </p>
                    <p>
                      To use package conversion:
                    </p>
                    <ol>
                      <li>Upload your DOCX document under Document Selection.</li>
                      <li>Upload the original course package ZIP under Brightspace ZIP Package.</li>
                      <li>Select the output folder on your computer.</li>
                      <li>Click <strong>Convert and Save ZIP</strong> to process your document.</li>
                    </ol>
                    <p>
                      DocWeb matches pages sequentially, replaces the target HTML documents, removes stale page copies from the package's <code>course-pages/</code> folder, and updates the resource references and titles inside <code>imsmanifest.xml</code> automatically.
                    </p>
                  </div>
                )}
                {wikiActiveTab === "saved-configs" && (
                  <div className="wiki-section">
                    <h3>Saved Configurations</h3>
                    <p>
                      Saved Configurations let you store a named snapshot of all your current settings and reload them in one click. This is useful when you regularly convert documents with different banner types, file prefixes, or visibility options and want to switch between setups quickly.
                    </p>

                    <h4>Opening the panel</h4>
                    <p>
                      Click the <strong>◀ Configs</strong> button in the top-left corner of the main card. The panel slides out to the left, similar to the Advanced Options drawer on the right.
                    </p>

                    <h4>Saving a configuration</h4>
                    <ol>
                      <li>Set up all your options on the main card (banner type, footer, prefix, checkboxes, visibility, etc.).</li>
                      <li>Open the Configs panel and type a name in the <strong>Configuration name</strong> field.</li>
                      <li>Click <strong>Save Current Settings</strong>. The name will immediately appear in the dropdown.</li>
                    </ol>
                    <div className="wiki-alert-info">
                      <p>
                        Saving under an existing name will overwrite that configuration. Configurations are stored in your browser's local storage and persist across sessions and page refreshes.
                      </p>
                    </div>

                    <h4>Loading a configuration</h4>
                    <p>
                      Select any saved name from the dropdown at the top of the Configs panel. All settings are applied immediately — no extra button click required.
                    </p>

                    <h4>Deleting a configuration</h4>
                    <p>
                      Select the configuration from the dropdown, then click <strong>Delete</strong>. The configuration is removed from local storage straight away.
                    </p>

                    <h4>Clearing all saved settings</h4>
                    <p>
                      Open <strong>Preferences</strong> (the gear icon, top-right) and click <strong>Clear All</strong>. You will be asked to confirm before anything is deleted — this removes every saved configuration as well as other stored DocWeb preferences.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showCssWarningModal && (
        <div className="css-warning-backdrop">
          <div className="css-warning-modal">
            <h2>No Custom CSS Selected</h2>
            <p>
              Are you sure you do not want to use a custom CSS?
            </p>
            <div className="checkbox-row">
              <input
                type="checkbox"
                id="dontShowCssWarning"
                checked={dontShowCssWarningCheckbox}
                onChange={(e) => setDontShowCssWarningCheckbox(e.target.checked)}
              />
              <label htmlFor="dontShowCssWarning">Do not show this warning again</label>
            </div>
            <div className="modal-actions">
              <button
                className="css-warning-btn cancel"
                onClick={() => setShowCssWarningModal(false)}
              >
                Cancel
              </button>
              <button
                className="css-warning-btn confirm"
                onClick={handleConfirmConversionWithoutCss}
              >
                Yes, Convert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer>
        <p>DocWeb Browser Edition</p>
        <span className="version-tag">v1.7.0</span>
      </footer>
    </div>
  );
}

export default App;
